import { isApiProblem } from "../api/problem";

/**
 * The shared form-error pattern (ruling 2026-10-03, "Edit deployment" screenshot): every form and dialog shows its
 * problems the same way —
 *   - a summary at the top, "Please fix N fields", whose lines are links that focus and scroll to their field;
 *   - each invalid field marked (red border via `aria-invalid="true"`, the label in red, an inline message under it
 *     wired with `aria-describedby`);
 *   - errors that track the values live: a field's error goes the moment the field becomes valid, so a fixed field can
 *     never stay listed;
 *   - server 422 `errors[].field` mapped onto the same fields; a field the form does not show stays in the summary.
 * This file is the pure half (no React), `useFormErrors` the state, `ErrorSummary` / `FieldMessage` the markup.
 */

/** One problem with one field, in words that name the fix. `field` is the form's own key for the field. */
export interface FieldIssue {
  field: string;
  message: string;
}

/** What a failed save said, split into what can be anchored to a field and what cannot. */
export interface ServerIssues {
  fieldIssues: FieldIssue[];
  /** Unmet rules and errors for fields this form does not show: listed in the summary without an anchor. */
  general: string[];
}

export function summaryTitle(count: number): string {
  return count === 1 ? "Please fix 1 field" : `Please fix ${count} fields`;
}

/** The inline message's id for a control id (the convention every form already used). */
export const errorIdOf = (controlId: string) => `${controlId}-error`;

/**
 * One issue per field (the first message wins), in on-screen order: `order` lists field keys top to bottom; a field it
 * does not name keeps its place after the named ones.
 */
export function orderIssues(issues: readonly FieldIssue[], order?: readonly string[]): FieldIssue[] {
  const seen = new Set<string>();
  const unique = issues.filter((issue) => (seen.has(issue.field) ? false : (seen.add(issue.field), true)));
  if (!order) return unique;
  const rank = (field: string) => {
    const index = order.indexOf(field);
    return index === -1 ? order.length : index;
  };
  return unique.map((issue, index) => ({ issue, index })).sort((a, b) => rank(a.issue.field) - rank(b.issue.field) || a.index - b.index).map((entry) => entry.issue);
}

/**
 * A server field name as a plain dotted path: "/customFields/impact" → "customFields.impact", "$.name" → "name".
 * "" when the server named no field. "body" stays "body": the server uses it both for a text field called body (a
 * comment, a service desk reply: "Write the comment text.") and for the whole request body (`invalid_json`), so
 * `serverIssuesOf` tells the two apart by the error's code, not by the name (D-R17-02).
 */
export function normaliseServerField(field: string | null | undefined): string {
  return (field ?? "").trim().replace(/^\$\.?/, "").replace(/^\/+/, "").replace(/\//g, ".");
}

/** The error is about the request as a whole (unreadable JSON), never about a field the person can fix. */
const WHOLE_BODY_CODES = new Set(["invalid_json"]);

/** The default mapping: a named field is the form's key, except "body", which a form shows only if it says so. */
const defaultMapField = (field: string) => (field && field !== "body" ? field : null);

/**
 * Split a failed save into field issues and summary-only lines. `mapField` turns the server's (normalised) field name
 * into the form's key, or null when the form does not show that field — its message then stays in the summary. Unmet
 * rules are never field-shaped, so they always stay in the summary. Anything that is not an API problem yields nothing
 * here; the caller shows its message as the summary's lead.
 */
export function serverIssuesOf(caught: unknown, mapField: (serverField: string) => string | null | undefined = defaultMapField): ServerIssues {
  if (!isApiProblem(caught)) return { fieldIssues: [], general: [] };
  const fieldIssues: FieldIssue[] = [];
  const general: string[] = [];
  for (const rule of caught.unmetRules) if (rule.message.trim()) general.push(rule.message.trim());
  for (const error of caught.fieldErrors) {
    const message = error.message.trim();
    if (!message) continue;
    const key = WHOLE_BODY_CODES.has(error.code) ? null : mapField(normaliseServerField(error.field));
    if (key) fieldIssues.push({ field: key, message });
    else general.push(message);
  }
  return { fieldIssues, general: [...new Set(general)] };
}

/** What a person types or picks into: preferred over a toolbar button that happens to come first in a field group. */
const CONTROLS = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]';
const FOCUSABLE = `${CONTROLS}, button:not([disabled]), [tabindex]`;

/**
 * Focus a field by its control id and scroll it into view inside whatever scrolls it (a dialog body, the page). A
 * fieldset or wrapper id focuses its first control; an element with nothing focusable inside gets tabindex -1 first.
 */
export function focusField(controlId: string, root: Document | HTMLElement = document): boolean {
  const element = root instanceof Document ? root.getElementById(controlId) : root.querySelector<HTMLElement>(`#${CSS.escape(controlId)}`);
  if (!element) return false;
  const focusable =
    element.matches(FOCUSABLE) && !element.matches("fieldset") ? element : (element.querySelector<HTMLElement>(CONTROLS) ?? element.querySelector<HTMLElement>(FOCUSABLE) ?? element);
  if (focusable === element && !element.matches(FOCUSABLE)) element.setAttribute("tabindex", "-1");
  focusable.focus({ preventScroll: true });
  // jsdom has no scrollIntoView; a real browser scrolls every scrolling ancestor, the dialog body included.
  element.scrollIntoView?.({ block: "center", inline: "nearest" });
  return true;
}
